/**
 * Marked for Exam — Status tab, step 2 (s2)
 * Figma: Status → Marked for Exam
 *
 * Sub-status tabs: Accepted | Objected | Accepted after Objection | Hearing after Objection
 * Accepted + Accepted after Objection show a Message Preview; the send action is
 * driven by the dashboard footer's "Send & Continue" button (mirrors FC Pass pattern).
 * A "Resend to WhatsApp" button appears inline after the message is already sent.
 */
import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { toast } from "sonner";
import { getWorkflowStatus } from "api/applications";
import {
  getLeadDetails,
  getApplicationsByStatus,
  getMarkedForExamPreview,
  sendMarkedForExamWhatsApp,
  getAcceptedAfterObjectionStatus,
  getAcceptedAfterObjectionInformClientPreview,
  sendAcceptedAfterObjectionInformClientWhatsApp,
  type ApplicationsByStatusItem,
  type AcceptedAfterObjectionStatus,
} from "api/discussion";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ExamSubStatus =
  | "accepted"
  | "objected"
  | "accepted-after-objection"
  | "hearing-after-objection";

type TmEntry = { tmNo: string; mark: string };

export type MarkedForExamViewProps = {
  leadId: string | null | undefined;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits the Accepted form */
  onAcceptedFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits the Accepted after Objection form */
  onAAOFooterSendModeChange?: (needsSend: boolean) => void;
};

export const MARKED_FOR_EXAM_ACCEPTED_FORM_ID = "marked-for-exam-accepted-form";
export const MARKED_FOR_EXAM_AAO_FORM_ID = "marked-for-exam-aao-form";

const ACCEPTED_STEP_SLUG = "S3_03B_INFORM_CLIENT_EXAMINATION_REPORT_ACCEPTED";
const AAO_STEP_SLUG = "S3_03C_III_INFORM_CLIENT_ACCEPTED_AFTER_OBJECTION";

const SUB_STATUS_TABS: { id: ExamSubStatus; label: string }[] = [
  { id: "accepted", label: "Accepted" },
];

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function RadioCircle({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="8" cy="8" r="8" fill={active ? "white" : "#6D6D6D"} />
      <path
        d="M4.5 8.2 L6.8 10.5 L11.6 5.3"
        stroke={active ? "#222222" : "white"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared message preview (no embedded send button — footer drives send)
// ---------------------------------------------------------------------------

function MessagePreview({
  loading,
  message,
  phone,
}: {
  loading: boolean;
  message: string | null;
  phone: string;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-sm font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        <div className="px-4 py-4 min-h-[80px]">
          {loading ? (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              No preview available
            </span>
          )}
        </div>

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResendButton({
  disabled,
  resending,
  onResend,
}: {
  disabled: boolean;
  resending: boolean;
  onResend: () => void;
}) {
  return (
    <div className="flex flex-row items-center gap-3 w-full">
      <button
        type="button"
        disabled={disabled || resending}
        onClick={onResend}
        className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {resending ? "Resending…" : "Resend to WhatsApp"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coming soon placeholder
// ---------------------------------------------------------------------------

function ComingSoonPanel({ label }: { label: string }) {
  return (
    <div
      className="flex flex-col gap-3"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <p className="text-sm text-[#6D6D6D]">{label} — Coming soon</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MarkedForExamView({
  leadId,
  onSuccess,
  onSavingChange,
  onAcceptedFooterSendModeChange,
  onAAOFooterSendModeChange,
}: MarkedForExamViewProps) {
  const [subStatus, setSubStatus] = useState<ExamSubStatus>("accepted");
  const [tmEntries, setTmEntries] = useState<TmEntry[]>([]);
  const [selectedTmIdx, setSelectedTmIdx] = useState(0);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(!!leadId);

  // Accepted panel state
  const [acceptedPreview, setAcceptedPreview] = useState<string | null>(null);
  const [acceptedPreviewLoading, setAcceptedPreviewLoading] = useState(false);
  const [hasSentAccepted, setHasSentAccepted] = useState(false);
  const [resendingAccepted, setResendingAccepted] = useState(false);

  // Accepted after Objection panel state
  const [aaoStatus, setAaoStatus] = useState<AcceptedAfterObjectionStatus | null>(null);
  const [aaoPreview, setAaoPreview] = useState<string[]>([]);
  const [aaoPreviewLoading, setAaoPreviewLoading] = useState(false);
  const [hasSentAAO, setHasSentAAO] = useState(false);
  const [resendingAAO, setResendingAAO] = useState(false);

  // Load lead details + TM entries
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setTmEntries([]);
      setPhone("");
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      getLeadDetails(leadId),
      getApplicationsByStatus("Marked for Exam", "NEW_APP", leadId).catch(() => null),
    ])
      .then(([leadRes, markedStatus]) => {
        if (cancelled) return;
        setPhone(leadRes.lead.phone ?? "");
        setTmEntries(
          (markedStatus?.applications ?? []).map((a: ApplicationsByStatusItem) => ({
            tmNo: a.tmNo,
            mark: a.mark,
          }))
        );
        setSelectedTmIdx(0);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load details"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Check workflow status for already-sent flags
  useEffect(() => {
    if (!leadId) {
      setHasSentAccepted(false);
      setHasSentAAO(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const accepted = status.steps.find((s) => s.slug === ACCEPTED_STEP_SLUG);
        const aao = status.steps.find((s) => s.slug === AAO_STEP_SLUG);
        setHasSentAccepted(Boolean(accepted?.isCompleted));
        setHasSentAAO(Boolean(aao?.isCompleted));
      } catch {
        if (!cancelled) {
          setHasSentAccepted(false);
          setHasSentAAO(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Reset selected index when switching sub-tabs to avoid out-of-bounds index
  useEffect(() => {
    setSelectedTmIdx(0);
  }, [subStatus]);

  // Load AAO status (for TM list on accepted-after-objection tab)
  useEffect(() => {
    if (!leadId || subStatus !== "accepted-after-objection") {
      setAaoStatus(null);
      return;
    }
    let cancelled = false;
    getAcceptedAfterObjectionStatus(leadId)
      .then((data) => {
        if (!cancelled) setAaoStatus(data);
      })
      .catch(() => {
        if (!cancelled) setAaoStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, subStatus]);


  // Load Accepted preview
  useEffect(() => {
    if (!leadId || subStatus !== "accepted") {
      setAcceptedPreview(null);
      setAcceptedPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setAcceptedPreviewLoading(true);
    getMarkedForExamPreview(leadId)
      .then(({ message }) => {
        if (!cancelled) setAcceptedPreview(message);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
          setAcceptedPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setAcceptedPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, subStatus]);

  // Load AAO preview
  useEffect(() => {
    if (!leadId || subStatus !== "accepted-after-objection") {
      setAaoPreview([]);
      setAaoPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setAaoPreviewLoading(true);
    getAcceptedAfterObjectionInformClientPreview(leadId)
      .then(({ messages }) => {
        if (!cancelled) setAaoPreview(messages ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
          setAaoPreview([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAaoPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, subStatus]);

  // Signal dashboard footer when this panel needs Send & Continue
  useLayoutEffect(() => {
    const needsSend =
      subStatus === "accepted" &&
      !hasSentAccepted &&
      !!acceptedPreview &&
      !!phone;
    onAcceptedFooterSendModeChange?.(needsSend);
  }, [
    subStatus,
    hasSentAccepted,
    acceptedPreview,
    phone,
    onAcceptedFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const needsSend =
      subStatus === "accepted-after-objection" &&
      !hasSentAAO &&
      aaoPreview.length > 0 &&
      !!phone;
    onAAOFooterSendModeChange?.(needsSend);
  }, [subStatus, hasSentAAO, aaoPreview, phone, onAAOFooterSendModeChange]);

  // Form submit handlers — triggered by dashboard footer "Send & Continue"
  const handleAcceptedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || hasSentAccepted) return;
    onSavingChange?.(true);
    try {
      await sendMarkedForExamWhatsApp(leadId);
      setHasSentAccepted(true);
      toast.success("Marked for Exam update sent via WhatsApp.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleAAOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || hasSentAAO) return;
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionInformClientWhatsApp(leadId);
      setHasSentAAO(true);
      toast.success("Accepted after Objection update sent via WhatsApp.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleResendAccepted = async () => {
    if (!leadId || resendingAccepted) return;
    setResendingAccepted(true);
    onSavingChange?.(true);
    try {
      await sendMarkedForExamWhatsApp(leadId);
      toast.success("Marked for Exam update resent.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingAccepted(false);
      onSavingChange?.(false);
    }
  };

  const handleResendAAO = async () => {
    if (!leadId || resendingAAO) return;
    setResendingAAO(true);
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionInformClientWhatsApp(leadId);
      toast.success("Accepted after Objection update resent.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingAAO(false);
      onSavingChange?.(false);
    }
  };

  // Each sub-tab sources its TM list from its own status endpoint to avoid
  // cross-status contamination (e.g. Accepted apps appearing in AAO tab).
  const displayTmEntries: TmEntry[] = useMemo(() => {
    if (subStatus === "accepted") return tmEntries;
    if (subStatus === "accepted-after-objection") {
      return (aaoStatus?.applications ?? []).map((a) => ({
        tmNo: a.tmNo,
        mark: a.mark,
      }));
    }
    return [];
  }, [subStatus, tmEntries, aaoStatus]);

  if (loading) {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  const aaoCombined = aaoPreview.join("\n\n---\n\n");

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* TM selector */}
      {displayTmEntries.length > 0 && (
        <div className="flex flex-row flex-wrap gap-3">
          {displayTmEntries.map((entry, idx) => {
            const isActive = selectedTmIdx === idx;
            return (
              <button
                key={entry.tmNo}
                type="button"
                onClick={() => setSelectedTmIdx(idx)}
                className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white border-transparent"
                    : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
                }`}
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "12px",
                  fontWeight: isActive ? 500 : 400,
                  lineHeight: "20px",
                }}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(entry.tmNo)}
              </button>
            );
          })}
        </div>
      )}

      {/* Content panel */}
      {(subStatus === "accepted" || subStatus === "accepted-after-objection") &&
      displayTmEntries.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-sm font-medium text-[#222222]">
            No applications with this status yet.
          </p>
          <p className="text-xs text-[#6D6D6D]">
            {subStatus === "accepted"
              ? "Applications will appear here once their status is set to Accepted."
              : "Applications will appear here once their status is set to Accepted after Objection."}
          </p>
        </div>
      ) : subStatus === "accepted" ? (
        <form
          id={MARKED_FOR_EXAM_ACCEPTED_FORM_ID}
          onSubmit={handleAcceptedSubmit}
          className="flex flex-col gap-3"
        >
          <MessagePreview
            loading={acceptedPreviewLoading}
            message={acceptedPreview}
            phone={phone}
          />
          {hasSentAccepted && leadId && (
            <ResendButton
              disabled={acceptedPreviewLoading}
              resending={resendingAccepted}
              onResend={() => void handleResendAccepted()}
            />
          )}
        </form>
      ) : subStatus === "accepted-after-objection" ? (
        <form
          id={MARKED_FOR_EXAM_AAO_FORM_ID}
          onSubmit={handleAAOSubmit}
          className="flex flex-col gap-3"
        >
          <MessagePreview
            loading={aaoPreviewLoading}
            message={aaoCombined || null}
            phone={phone}
          />
          {hasSentAAO && leadId && (
            <ResendButton
              disabled={aaoPreviewLoading}
              resending={resendingAAO}
              onResend={() => void handleResendAAO()}
            />
          )}
        </form>
      ) : subStatus === "objected" ? (
        <ComingSoonPanel label="Objected" />
      ) : (
        <ComingSoonPanel label="Hearing after Objection" />
      )}
    </div>
  );
}
